import { singleton } from "tsyringe";
import type { ElasticsearchGateway, ESCursor } from "@/interface/gateway";
import type { FormFields as SearchPokemonSingleProductFields } from "@/forms/pokemon/searchSingleProduct";
import type { SearchResponse } from "./types";
import type { ESPokemonSingleProduct } from "@ka-nabellinc/tcg-platform-es-schema";
import { buildPokemonSingleProductQuery } from "./builders";
import { PublishFromQuery, PublishToShouldQuery, PokemonSingleDefaultSort, LowestAvailablePriceSort, HighestAvailablePriceSort } from './const'

const DEFAULT_RESULT_SIZE = 30;
const DEFAULT_MAX_SIZE = 10000; // elasticsearch デフォルトの取得件数上限値

function isCursor(value: unknown): value is ESCursor {
  return Array.isArray(value) && value.every((v) => (
    typeof v === 'string' || typeof v === 'number' || v === null
  ));
}

export type ProductTypeIds = {
  COMBO_PRODUCT: string

  POKEMON_PICKUP_GENERAL_PURPOSE_PRODUCT: string
  POKEMON_PICKUP_BASIC_ENERGY_PRODUCT: string
  POKEMON_PICKUP_SAR_PRODUCT: string
  POKEMON_PICKUP_LATEST_PACK_PRODUCT: string
  POKEMON_PICKUP_EXTRA: string
}

@singleton()
export class ElasticsearchGatewayImpl implements ElasticsearchGateway {
  private POKEMON_SINGLE_INDEX_NAME = "pokemon-single-products";

  constructor(private endpoint: string, private apiKey: string, private productTypeIds: ProductTypeIds) {}

  async ping(): Promise<boolean> {
    try {
      await $fetch(`${this.endpoint}/_cluster/health?local=true&timeout=10s`, {
        headers: this.headers,
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  get POKEMON_SINGLE_INDEX_BASE() {
    return `${this.endpoint}/${this.POKEMON_SINGLE_INDEX_NAME}`;
  }

  get headers() {
    return {
      authorization: `ApiKey ${this.apiKey}`,
      "Content-Type": "application/x-ndjson",
    };
  }

  async searchPokemonSingleProduct(
    params: SearchPokemonSingleProductFields,
    page: number
  ) {
    const query = buildPokemonSingleProductQuery(params);
    const results = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query,
          sort: PokemonSingleDefaultSort,
          size: DEFAULT_RESULT_SIZE,
          from: (page - 1) * DEFAULT_RESULT_SIZE,
        },
      }
    );

    const {
      hits: { total, hits },
    } = results;
    return {
      hits: hits.map((hit) => hit._source),
      total: total.value,
    };
  }

  async getPokemonSingleProduct(inventoryProductId: string) {
    const { hits: { hits } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    inventory_product_id: inventoryProductId,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          size: 1,
        },
      }
    );

    if (!hits.length) {
      throw new Error(`pokemon single product not found: ${inventoryProductId}`)
    }

    return hits[0]._source
  }

  async getPokemonSingleProductsByPackId(packId: string) {
    const {
      hits: { hits },
    } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                {
                  term: {
                    "pm_card.pack.pack_id": parseInt(packId, 10),
                  },
                },
                PublishFromQuery,
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          // 検索表示順の仕様: https://docs.google.com/spreadsheets/d/1Hh1M49TT8sJRGC0i6X5rvPWk296CGsKNhIbtDx4p7Hk/edit#gid=215472697&range=L29
          sort: [
            { "pm_card.rarity.rarity_sort_for_ec_customer": { order: "asc" } },
            { "pm_card.collection_number": { order: "asc" } },
            { created_at: { order: "desc" } },
          ],
          size: DEFAULT_MAX_SIZE,
        },
      }
    );

    return hits.map((hit) => hit._source);
  }

  async getLastSoldPokemonSingleProducts(size: number, searchAfter?: ESCursor) {
    // 検索仕様: https://docs.google.com/document/d/1E_WSvv1fYmsxXghTmiH-fHPLy91ZsXYQNWAWq8fuR0o/edit#heading=h.m1rup2myk190
    const results = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                {
                  range: {
                    last_sold_at: {
                      gte: 'now-6d',
                    },
                  },
                },
                {
                  range: {
                    shop_shelf_inventory_num: {
                      gte: 1,
                    },
                  },
                },
                PublishFromQuery,
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: [
            { last_sold_at: { order: "desc" } },
            { "pm_card.series.created_at": { order: "desc" } },
            { "pm_card.pack.released_at": { order: "desc" } },
            { "pm_card.pack.created_at": { order: "asc" } },
            { "pm_card.rarity.rarity_sort_for_ec_customer": { order: "asc" } },
            { "pm_card.collection_number": { order: "asc" } },
            { created_at: { order: "asc" } },
          ],
          size,
          search_after: isCursor(searchAfter) ? searchAfter : undefined,
        },
      }
    );

    const {
      hits: { total, hits },
    } = results;

    const lastSort = hits.length ? hits[hits.length - 1].sort : undefined

    return {
      hits: hits.map((hit) => hit._source),
      total: total.value,
      cursor: lastSort,
    }
  }

  async getSamePokemonSingleProducts(data: ESPokemonSingleProduct, size: number, from: number) {
    const {
      inventory_product_id,
      pm_card: {
        pm_card_classification,
        pm_card_pokemon_id,
        pm_card_trainer_id,
        pm_card_energy_id
      }
    } = data

    if (pm_card_classification === 'pokemon' && pm_card_pokemon_id) {
      return this.getPokemonSingleProductsByPokemonId(pm_card_pokemon_id, inventory_product_id, size, from)
    } else if (pm_card_classification === 'trainer' && pm_card_trainer_id) {
      return this.getPokemonSingleProductsByTrainerId(pm_card_trainer_id, inventory_product_id, size, from)
    } else if (pm_card_classification === 'energy' && pm_card_energy_id) {
      return this.getPokemonSingleProductsByEnergyId(pm_card_energy_id, inventory_product_id, size, from)
    } else {
      return {
        hits: [],
        total: 0
      }
    }
  }

  async getPokemonSingleProductsByPokemonId(pmCardPokemonId: number, excludeInventoryProductId: string, size: number, from: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "pm_card.pm_card_pokemon_id": pmCardPokemonId,
                  },
                },
              ],
              must_not: [
                { term: { inventory_product_id: excludeInventoryProductId } }
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: [
            LowestAvailablePriceSort,
            ...PokemonSingleDefaultSort
          ],
          size,
          from,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSingleProductsByTrainerId(pmCardTrainerId: number, excludeInventoryProductId: string, size: number, from: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "pm_card.pm_card_trainer_id": pmCardTrainerId,
                  },
                },
              ],
              must_not: [
                { term: { inventory_product_id: excludeInventoryProductId } }
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: [
            LowestAvailablePriceSort,
            ...PokemonSingleDefaultSort
          ],
          size,
          from,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSingleProductsByEnergyId(pmCardEnergyId: number, excludeInventoryProductId: string, size: number, from: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "pm_card.pm_card_energy_id": pmCardEnergyId,
                  },
                },
              ],
              must_not: [
                { term: { inventory_product_id: excludeInventoryProductId } }
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: [
            LowestAvailablePriceSort,
            ...PokemonSingleDefaultSort
          ],
          size,
          from,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSingleProductsByPokemonUniqueId(pokemonUniqueId: number, size: number, from: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "pm_card.pm_card_pokemon_unique_id": pokemonUniqueId,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort,
          size,
          from,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSingleProductsByPreEvolutionPokemonUniqueId(pokemonUniqueId: number, size: number, from: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "pm_card.pm_card_pre_evolution_pokemon_unique_id": pokemonUniqueId,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort,
          size,
          from,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonComboLikeSingleProducts(size?: number, from?: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.COMBO_PRODUCT,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort,
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupGeneralPurposeProducts(size?: number, from?: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.POKEMON_PICKUP_GENERAL_PURPOSE_PRODUCT,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort, // FIXME: 汎用カードソート順をMDと議論
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupBasicEnergyProducts(size?: number, from?: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.POKEMON_PICKUP_BASIC_ENERGY_PRODUCT,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort, // FIXME: ソート順をMDと議論
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupSARProducts(size?: number, from?: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.POKEMON_PICKUP_SAR_PRODUCT,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort, // FIXME: ソート順をMDと議論
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupLatestPackProducts(packId: number, size?: number, from?: number) {
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.POKEMON_PICKUP_LATEST_PACK_PRODUCT,
                  },
                },
                {
                  term: {
                    "pm_card.pack.pack_id": packId,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: PokemonSingleDefaultSort, // FIXME: ソート順をMDと議論
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupExtraProducts(params: { size?: number, from?: number, sortType?: 'default' | 'availablePriceDesc' } = {}) {
    const { size, from, sortType = 'default' } = params
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  term: {
                    "product_type_ids": this.productTypeIds.POKEMON_PICKUP_EXTRA,
                  },
                },
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: sortType === 'default' ? PokemonSingleDefaultSort : HighestAvailablePriceSort,
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }

  async getPokemonSinglePickupPriceDownProducts(params: { size?: number, from?: number } = {}) {
    const { size, from } = params
    const { hits: { hits, total } } = await $fetch<SearchResponse<ESPokemonSingleProduct>>(
      `${this.POKEMON_SINGLE_INDEX_BASE}/_search`,
      {
        method: "POST",
        headers: this.headers,
        body: {
          query: {
            bool: {
              must: [
                PublishFromQuery,
                {
                  script: {
                    script: {
                      source: `
                        def s = doc['rank_s_inventory.inventory_num'].size()!=0 ? doc['rank_s_inventory.inventory_num'].value : 0;
                        def a = doc['rank_a_inventory.inventory_num'].size()!=0 ? doc['rank_a_inventory.inventory_num'].value : 0;
                        def b = doc['rank_b_inventory.inventory_num'].size()!=0 ? doc['rank_b_inventory.inventory_num'].value : 0;
                        def c = doc['rank_c_inventory.inventory_num'].size()!=0 ? doc['rank_c_inventory.inventory_num'].value : 0;
                        return s > 0 || a > 0 || b > 0 || c > 0;
                      `
                    }
                  }
                },
                {
                  range: {
                    "latest_selling_price_pricing_history.created_at": { gte: "now-30d" }
                  }
                },
                { exists: { field: "latest_selling_price_pricing_history.updated_price" } },
                { exists: { field: "latest_selling_price_pricing_history.previous_price" } },
                {
                  script: {
                    script: {
                      source: "return doc['latest_selling_price_pricing_history.updated_price'].value < doc['latest_selling_price_pricing_history.previous_price'].value;"
                    }
                  }
                }
              ],
              should: PublishToShouldQuery,
              minimum_should_match: 1,
            },
          },
          sort: [
            { "latest_selling_price_pricing_history.created_at": { order: "desc" } },
            ...PokemonSingleDefaultSort
          ],
          size: typeof size === 'number' ? size : DEFAULT_MAX_SIZE,
          from: typeof from === 'number' ? from : 0,
        },
      }
    );

    return {
      hits: hits.map(hit => hit._source),
      total: total.value
    }
  }
}
